import React, {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  createLaunchDarklyContext,
  LaunchDarklyContextValue,
} from "@adsk/alloy-provider";

import { getEnvValue } from "../env";

type FeatureFlags = {
  "model-polling-times": {
    shortWaitTime: number;
    longWaitTime: number;
    batchCountModelObjects?: number;
    fetchModelAssetDiffs?: number;
    fetchModelFields?: number;
    fetchModelFieldValues?: number;
    fetchModelMappingConflicts?: number;
    queryModelObjects?: number;
    batchGetProcessingStatus: number;
  };
  "status-statistics": boolean;
  "assets-export-xlsx-limit": number;
  "api-assets-per-project-limit": number;
  "api-assets-per-import-operation-limit": number;
  "enable-read-only-mode": boolean;
  "enable-system-export": boolean;
  "enable-system-categories-in-project-templates": boolean;
  "legacy-form-templates": boolean;
  "asset-model-linking": boolean;
  "enable-display-trashed-models": boolean;
  "enable-bulk-edit-asset-status-cf-all-categories": boolean;
  "enable-asset-status-percent-complete": boolean;
  "enable-asset-list-saved-filters": boolean;
  "enable-asset-classifications": boolean;
};

export const flagDefaults: FeatureFlags = {
  "model-polling-times": {
    shortWaitTime: 2000,
    longWaitTime: 10000,
    batchGetProcessingStatus: 60000,
  },
  "status-statistics": false,
  "assets-export-xlsx-limit": 999999,
  "api-assets-per-project-limit": 500000,
  "api-assets-per-import-operation-limit": 500000,
  "enable-read-only-mode": false,
  "enable-system-export": false,
  "enable-system-categories-in-project-templates": false,
  "legacy-form-templates": false,
  "asset-model-linking": false,
  "enable-display-trashed-models": false,
  "enable-bulk-edit-asset-status-cf-all-categories": false,
  "enable-asset-status-percent-complete": false,
  "enable-asset-list-saved-filters": false,
  "enable-asset-classifications": false,
};

export type FeatureFlag<K extends keyof FeatureFlags = keyof FeatureFlags> = {
  key: K;
  value: FeatureFlags[K];
};

export const LaunchDarklyContext = createContext<
  LaunchDarklyContextValue | undefined
>(undefined);

const {
  LaunchDarklyProvider,
  useFeatureFlag: alloyUseFeatureFlag,
  useAllFeatureFlags,
} = createLaunchDarklyContext(LaunchDarklyContext);

export { useAllFeatureFlags };

export const useFeatureFlag = <K extends keyof FeatureFlags>(key: K) => {
  const { value } = alloyUseFeatureFlag<FeatureFlags[K]>(
    key,
    flagDefaults[key]
  );
  return value;
};

export type FeatureFlagProviderProps = PropsWithChildren<{
  apiKey?: string;
  loadingFallback?: ReactNode;
  /**
   * Render `loadingFallback` until the first flag payload arrives (default). Set
   * `false` to render children immediately, reading flag defaults until the
   * payload lands — appropriate where blanking the surface is worse than a late
   * flag, e.g. a drawer inside someone else's page.
   */
  gateOnLoading?: boolean;
  /**
   * Also publish this provider's context to `OutOfTreeFeatureFlagProvider`, so
   * surfaces a host renders outside this provider's React tree can read flags.
   */
  shareOutOfTree?: boolean;
}>;

export const FeatureFlagProvider: FC<FeatureFlagProviderProps> = ({
  apiKey,
  loadingFallback = null,
  gateOnLoading = true,
  shareOutOfTree = false,
  children,
}) => {
  return (
    <LaunchDarklyProvider
      apiKey={apiKey ?? getEnvValue("LAUNCH_DARKLY_CLIENT_ID")}
    >
      {shareOutOfTree && <OutOfTreePublisher />}
      {gateOnLoading ? (
        <FeatureFlagProviderWrapper loadingFallback={loadingFallback}>
          {children}
        </FeatureFlagProviderWrapper>
      ) : (
        children
      )}
    </LaunchDarklyProvider>
  );
};

const FeatureFlagProviderWrapper: FC<
  PropsWithChildren<{ loadingFallback: ReactNode }>
> = ({ loadingFallback, children }) => {
  const { loading } = useAllFeatureFlags();
  if (loading) return <>{loadingFallback}</>;
  return <>{children}</>;
};

/**
 * Module-level mirror of the live LaunchDarkly context value.
 *
 * React context only reaches descendants, so a host that renders our components
 * inside its own tree cannot be handed the flag context by nesting a provider —
 * which is the situation for every module-federation surface whose components
 * are registered with the host rather than rendered by us. `@adsk/alloy-provider`
 * bridges its own LaunchDarkly context through a `window`-level store for the
 * same reason; this is the equivalent for our LaunchDarkly project, scoped to
 * this bundle because that is exactly the boundary the value is valid within.
 *
 * A registry rather than a single slot because several roots can publish at once
 * — acc-viewer mounts the 2D and 3D Forma Assets remotes together on a 3D page —
 * and one unmounting must not blank the others. Any live entry will do: every
 * publisher in this bundle resolves the same LaunchDarkly project, environment
 * and user, so they are interchangeable.
 */
type OutOfTreeEntry = { value?: LaunchDarklyContextValue };

const outOfTreePublishers = new Set<OutOfTreeEntry>();
const outOfTreeListeners = new Set<() => void>();

const currentOutOfTreeValue = (): LaunchDarklyContextValue | undefined => {
  for (const { value } of outOfTreePublishers) {
    if (value) return value;
  }
  return undefined;
};

const notifyOutOfTreeListeners = () =>
  outOfTreeListeners.forEach((notify) => notify());

const OutOfTreePublisher: FC = () => {
  const value = useContext(LaunchDarklyContext);
  const entryRef = useRef<OutOfTreeEntry>();
  if (!entryRef.current) entryRef.current = {};

  // Registration is keyed to the instance, not the value, so a value change
  // never deregisters and re-registers (which would publish a transient gap).
  useEffect(() => {
    const entry = entryRef.current as OutOfTreeEntry;
    outOfTreePublishers.add(entry);
    return () => {
      outOfTreePublishers.delete(entry);
      notifyOutOfTreeListeners();
    };
  }, []);

  useEffect(() => {
    (entryRef.current as OutOfTreeEntry).value = value;
    notifyOutOfTreeListeners();
  }, [value]);

  return null;
};

/**
 * Re-provides the flag context published by a `FeatureFlagProvider` mounted with
 * `shareOutOfTree`, for a subtree the host renders outside that provider. Never
 * gates on loading: flags read their defaults until the payload arrives.
 *
 * Falls back to whatever provider is already above it, so wrapping a component
 * that turns out to be rendered in-tree after all is a no-op rather than a
 * downgrade to defaults.
 */
export const OutOfTreeFeatureFlagProvider: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const ambient = useContext(LaunchDarklyContext);
  const [published, setPublished] = useState(() => currentOutOfTreeValue());
  useEffect(() => {
    const notify = () => setPublished(currentOutOfTreeValue());
    outOfTreeListeners.add(notify);
    notify();
    return () => {
      outOfTreeListeners.delete(notify);
    };
  }, []);
  return (
    <LaunchDarklyContext.Provider value={published ?? ambient}>
      {children}
    </LaunchDarklyContext.Provider>
  );
};
